/*
  Shows the product's real compare-at price inside the Recharge Subscription Widget.

  The widget only knows variant.price and the selling plan's discount percent, so the
  strikethrough it renders on its own is the variant price, not compare-at, and the
  one-time row gets no strikethrough or badge at all. This walks the widget's (open)
  shadow roots after each render and rewrites those bits against compare_at_price.

  Data comes from snippets/ijw-subscription-compare-price.liquid.
*/
(function () {
  var DATA_ID = 'IjwCompareAtData';
  var WIDGET_TAG = 'recharge-subscription-widget';
  var BADGE_TEXT = 'Save up to {percent}%';
  var BENEFIT_TEXT = 'Save {percent}%';

  var data = null;
  var applying = false;
  var frame = null;

  function getData() {
    if (data) return data;
    var el = document.getElementById(DATA_ID);
    if (!el) return null;
    try {
      data = JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
    return data;
  }

  // Money strings here are theme-rendered (money / money_with_currency), so a
  // dot decimal separator with optional thousands commas is what we expect.
  function toCents(str) {
    if (!str) return null;
    var cleaned = String(str).replace(/[^\d.,]/g, '').replace(/,/g, '');
    if (!cleaned) return null;
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : Math.round(value * 100);
  }

  function percentOff(compare, current) {
    if (!compare || current == null || compare <= current) return null;
    return Math.floor(((compare - current) / compare) * 100);
  }

  // The widget pads prices with visually-hidden labels ("Discounted price:"), which
  // would otherwise end up in the parsed amount.
  function visibleText(el) {
    var out = '';
    if (!el) return out;
    Array.prototype.forEach.call(el.childNodes, function (node) {
      if (node.nodeType === 3) {
        out += node.textContent;
      } else if (node.nodeType === 1 && !node.classList.contains('visually-hidden')) {
        out += visibleText(node);
      }
    });
    return out.trim();
  }

  function setText(el, text) {
    if (el && el.textContent.trim() !== text) el.textContent = text;
  }

  // Stash the widget's own wording once, so a variant switch can restore it.
  function remember(el) {
    if (!el.hasAttribute('data-ijw-original')) {
      el.setAttribute('data-ijw-original', el.textContent.trim());
    }
  }

  // Elements we touch live across several nested shadow roots.
  function patchedRoots(shadowRoot) {
    var roots = [shadowRoot];
    Array.prototype.forEach.call(
      shadowRoot.querySelectorAll('rc-benefits, rc-selling-plans'),
      function (host) {
        if (host.shadowRoot) roots.push(host.shadowRoot);
      }
    );
    return roots;
  }

  // Related products and quick-add each carry their own id input, so prefer the
  // form inside the main product section the widget belongs to.
  var IGNORED_FORMS = 'quick-add-product, related-products, recently-viewed, .grid-item';

  function findVariantInput(scope) {
    var inputs = scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      // The Shop Pay installment form mirrors the same input.
      if (input.form && input.form.hasAttribute('data-product-form-installment')) continue;
      if (input.closest(IGNORED_FORMS)) continue;
      if (input.value) return String(input.value);
    }
    return null;
  }

  function activeVariantId(widget) {
    var section = widget.closest('.shopify-section, [data-section-id]');
    return (
      (section && findVariantInput(section)) ||
      findVariantInput(document) ||
      String(widget.getAttribute('default-variant-id') || '')
    );
  }

  function patchRow(row, compare) {
    var prices = row.querySelector('.rc-purchase-option__prices');
    if (!prices) return;

    var current =
      prices.querySelector('[part~="rc-purchase-option__discounted-price"]') ||
      prices.querySelector('[part~="rc-purchase-option__price"]');
    if (!current) return;

    var percent = percentOff(compare.amount, toCents(visibleText(current)));
    if (percent === null) return;

    // The subscription row already has a strikethrough (showing the wrong number);
    // the one-time row has none, so build one using the widget's own classes.
    var strike = prices.querySelector('[part~="rc-purchase-option__original-price"]');
    if (!strike) {
      strike = document.createElement('span');
      strike.className = 'rc-price strike-through text-muted';
      strike.setAttribute('part', 'rc-purchase-option__original-price');
      strike.setAttribute('data-ijw-compare-at', '');
      prices.insertBefore(strike, prices.firstChild);
    } else {
      remember(strike);
    }
    setText(strike, compare.formatted);

    var badge = row.querySelector('.rc-purchase-option__badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'rc-purchase-option__badge';
      badge.setAttribute('part', 'rc-purchase-option__badge');
      badge.setAttribute('data-ijw-badge', '');
      badge.id = 'ijw-badge-' + (row.className.indexOf('onetime') > -1 ? 'onetime' : 'subscription');
      row.insertBefore(badge, row.firstChild);

      var input = row.querySelector('.rc-purchase-option__input');
      if (input && !input.getAttribute('aria-describedby')) {
        input.setAttribute('aria-describedby', badge.id);
      }
    }
    setText(badge, BADGE_TEXT.replace('{percent}', percent));

    return percent;
  }

  // The savings line in the benefits list and the per-plan discount label are both
  // rendered from the plan percent, so they disagree with a compare-at figure.
  function patchPlanPercentLabels(row, percent) {
    if (percent === null || percent === undefined) return;

    var benefits = row.querySelector('rc-benefits');
    if (benefits && benefits.shadowRoot) {
      Array.prototype.forEach.call(benefits.shadowRoot.querySelectorAll('li'), function (li) {
        if (/^save\s+\d+(\.\d+)?%/i.test(li.textContent.trim())) {
          remember(li);
          setText(li, BENEFIT_TEXT.replace('{percent}', percent));
        }
      });
    }

    var plans = row.querySelector('rc-selling-plans');
    if (plans && plans.shadowRoot) {
      Array.prototype.forEach.call(
        plans.shadowRoot.querySelectorAll('.rc-plans-button__discount'),
        function (el) {
          var text = el.textContent.trim();
          if (!text || !/\d+(\.\d+)?%/.test(text)) return;
          remember(el);
          setText(el, text.replace(/\d+(\.\d+)?%/, percent + '%'));
        }
      );
    }
  }

  // Undo everything, for when the shopper lands on a variant with no compare-at.
  function revert(shadowRoot) {
    patchedRoots(shadowRoot).forEach(function (root) {
      Array.prototype.forEach.call(
        root.querySelectorAll('[data-ijw-compare-at], [data-ijw-badge]'),
        function (el) {
          el.parentNode.removeChild(el);
        }
      );
      Array.prototype.forEach.call(root.querySelectorAll('[data-ijw-original]'), function (el) {
        setText(el, el.getAttribute('data-ijw-original'));
        el.removeAttribute('data-ijw-original');
      });
    });
  }

  function apply(widget) {
    if (applying || !widget || !widget.shadowRoot) return;

    var all = getData();
    if (!all) return;

    var variant = all[activeVariantId(widget)];
    var onSale = variant && variant.compare && variant.compare > variant.price;

    applying = true;
    try {
      if (!onSale) {
        revert(widget.shadowRoot);
        return;
      }

      var compare = { amount: variant.compare, formatted: variant.compareFormatted };
      Array.prototype.forEach.call(
        widget.shadowRoot.querySelectorAll('.rc-purchase-option'),
        function (row) {
          patchPlanPercentLabels(row, patchRow(row, compare));
        }
      );
    } finally {
      applying = false;
    }
  }

  function schedule(widget) {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(function () {
      frame = null;
      apply(widget);
    });
  }

  function observe(widget) {
    if (widget.__ijwCompareAtBound) return;
    widget.__ijwCompareAtBound = true;

    var run = function () {
      schedule(widget);
    };

    new MutationObserver(run).observe(widget.shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Nested shadow roots (benefits, selling plans) render independently of the
    // parent's mutations, and variant swaps come through the theme's form.
    document.addEventListener('change', run);
    document.addEventListener('variant:change', run);
    document.addEventListener('product:variant-change', run);

    apply(widget);
  }

  function watch() {
    var start = Date.now();
    var timer = setInterval(function () {
      var widget = document.querySelector(WIDGET_TAG);
      if (widget && widget.shadowRoot) {
        observe(widget);
        schedule(widget);
      }
      if (Date.now() - start > 30000) clearInterval(timer);
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
